import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  agruparCombinaciones,
  parseIntegerField,
  sinClaves,
  type VarianteCombinacion,
} from './products-payload';

/**
 * Inventario por combinación (21-sep-2026).
 *
 * Un producto con variantes (Talla × Género × Color) no tiene UN stock: tiene uno por combinación. Aquí
 * se reparte ese stock: la matriz se genera sola desde las variantes cargadas arriba y se lee agrupada
 * por el primer eje, con **la cantidad al lado de cada variante**:
 *
 *   Talla 42
 *     Dama     [ 5 ]
 *     Hombre   [ 2 ]
 *   Talla 43
 *     Dama     [ 0 ]
 *     Hombre   [ 1 ]
 *
 * Cantidad vacía = esa combinación no tiene control de stock. Es un componente controlado: el formulario
 * guarda un mapa `clave canónica → cantidad (texto)` y aquí solo se pinta y se avisa de los cambios.
 *
 * OJO con el ancho: `Input` del sistema de diseño es de ancho completo (`w-full`), así que cada campo va
 * envuelto en un contenedor de ancho fijo (`w-24 shrink-0`) para que la etiqueta de la izquierda tenga su
 * espacio (si se le pasa `className="w-24"` al `Input`, ensancha el `<input>` pero la etiqueta se colapsa).
 */

export interface VariantInventoryFieldsProps {
  /** Combinaciones que genera `combinacionesVariantes` (puede venir vacía). */
  combos: VarianteCombinacion[];
  /** Mapa `clave → cantidad en texto`. */
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  disabled?: boolean;
  /** La tabla se cortó porque el producto supera el tope de combinaciones. */
  truncada?: boolean;
  className?: string;
}

export function VariantInventoryFields({
  combos,
  values,
  onChange,
  disabled = false,
  truncada = false,
  className,
}: VariantInventoryFieldsProps) {
  const [cantidadParaTodas, setCantidadParaTodas] = useState('');

  function setCantidad(clave: string, cantidad: string) {
    onChange({ ...values, [clave]: cantidad });
  }

  function aplicarATodas() {
    const texto = cantidadParaTodas.trim();
    if (texto.length === 0) return;

    const siguiente: Record<string, string> = { ...values };
    for (const combinacion of combos) siguiente[combinacion.clave] = texto;
    onChange(siguiente);
  }

  function vaciarTodas() {
    // Solo se vacían las combinaciones de ESTA matriz: las cantidades que el panel pide al lado de cada
    // variante (talla 40 → 5) viven en el MISMO mapa y no se tocan.
    onChange(sinClaves(values, combos.map((combinacion) => combinacion.clave)));
  }

  if (combos.length === 0) {
    return (
      <p className={`text-caption text-ink-muted ${className ?? ''}`.trim()}>
        Cargá primero las variantes en <em>Datos para la orden</em> (nombre y opciones) para repartir el
        inventario por combinación. Sin combinaciones se usa el inventario general de arriba.
      </p>
    );
  }

  const grupos = agruparCombinaciones(combos);
  const rellenadas = combos.filter((combinacion) => (values[combinacion.clave] ?? '').trim().length > 0);
  const total = rellenadas.reduce(
    (suma, combinacion) => suma + (parseIntegerField(values[combinacion.clave] ?? '') ?? 0),
    0,
  );

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-24 shrink-0">
          <Input
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            placeholder="ej. 3"
            disabled={disabled}
            aria-label="Cantidad para todas las combinaciones"
            value={cantidadParaTodas}
            onChange={(event) => setCantidadParaTodas(event.target.value)}
          />
        </span>

        <Button variant="secondary" size="sm" disabled={disabled} onClick={aplicarATodas}>
          Aplicar a todas
        </Button>

        <Button variant="ghost" size="sm" disabled={disabled} onClick={vaciarTodas}>
          Vaciar todas
        </Button>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <span className="flex-1" aria-hidden="true" />
        <span className="w-24 shrink-0 text-center text-caption font-medium text-ink-soft">Unidades</span>
      </div>

      <div className="mt-1 space-y-3">
        {grupos.map((grupo) => (
          <div key={grupo.titulo || 'combinaciones'}>
            {grupo.titulo ? (
              <p className="mb-1 text-caption font-medium text-ink-soft">{grupo.titulo}</p>
            ) : null}

            <div className="space-y-2">
              {grupo.filas.map((fila) => (
                <div key={fila.clave} className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate text-body text-ink" title={fila.etiqueta}>
                    {fila.etiqueta}
                  </span>

                  <span className="w-24 shrink-0">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      placeholder="Sin control"
                      disabled={disabled}
                      aria-label={`Cantidad de ${grupo.titulo} ${fila.etiqueta}`.trim()}
                      value={values[fila.clave] ?? ''}
                      onChange={(event) => setCantidad(fila.clave, event.target.value)}
                    />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 text-caption text-ink-muted">
        {rellenadas.length > 0
          ? `${rellenadas.length} de ${combos.length} combinaciones con cantidad · ${total} unidades en total`
          : 'Ninguna combinación tiene cantidad: este producto se venderá sin control por combinación.'}
      </p>

      {truncada ? (
        <p className="mt-1 text-caption text-warning">
          Hay más combinaciones que el máximo permitido ({combos.length} mostradas): reducí las opciones de
          las variantes para poder guardar el inventario por combinación.
        </p>
      ) : null}
    </div>
  );
}

